import { z } from 'zod/v4';
import { stringToBooleanSchema } from '../common/index';
import { orderTargetTypeEnum } from '../enums/index';

export const orderItemSchema = z.object({
  targetType: orderTargetTypeEnum,
  targetId: z.number()
    .int('ID товара должен быть целым числом')
    .positive('ID товара должен быть положительным'),
  quantity: z.number()
    .int('Количество должно быть целым числом')
    .positive('Количество должно быть положительным')
    .default(1),
  pricingTierId: z.number().int().positive().optional(),
  unitPriceOverride: z.number()
    .nonnegative('Переопределённая цена не может быть отрицательной')
    .optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const createOrderSchema = z.object({
  userId: z.number()
    .int('ID пользователя должен быть целым числом')
    .positive('ID пользователя должен быть положительным'),
  items: z.array(orderItemSchema).min(1, 'Заказ должен содержать хотя бы один товар'),
  promoCode: z.string().trim().min(1).max(100).optional(),
  // Preview-заказ для обновления вместо создания нового (чекаут: применение/
  // очистка промокода на уже созданном preview-заказе). Игнорируется, если
  // заказ уже оплачен, принадлежит другому юзеру или не найден — тогда
  // создаётся новый заказ (fallback), см. orders.service.ts createOrder.
  orderId: z.number().int().positive().optional(),
});

// Тело эндпоинта POST /me/orders — юзер создаёт/обновляет свой preview-заказ.
// userId не входит (берётся из токена); unitPriceOverride недоступен юзеру
// (только для админского createOrderSchema с adminUserId в orders.service.ts).
export const createMyOrderSchema = z.object({
  items: z.array(orderItemSchema.omit({ unitPriceOverride: true })).min(1, 'Заказ должен содержать хотя бы один товар'),
  promoCode: z.string().trim().min(1).max(100).optional(),
  orderId: z.number().int().positive().optional(),
});

export const updateOrderSchema = z.object({
  statusConfigId: z.number()
    .int('ID конфигурации статуса должен быть целым числом')
    .positive('ID конфигурации статуса должен быть положительным'),
});

export const orderFiltersSchema = z.object({
  statusConfigId: z.coerce.number().int('ID конфигурации статуса должен быть целым числом').positive('ID конфигурации статуса должен быть положительным').optional(),
  userId: z.coerce.number().int('ID пользователя должен быть целым числом').positive('ID пользователя должен быть положительным').optional(),
  assignedAdminId: z.coerce.number().int('ID администратора должен быть целым числом').positive('ID администратора должен быть положительным').optional(),
  targetType: orderTargetTypeEnum.optional(),
  targetId: z.coerce.number().int('ID товара должен быть целым числом').positive('ID товара должен быть положительным').optional(),
  pricingTierId: z.coerce.number().int('ID тарифа должен быть целым числом').positive('ID тарифа должен быть положительным').optional(),
  totalFrom: z.coerce.number().nonnegative('Минимальная сумма не может быть отрицательной').optional(),
  totalTo: z.coerce.number().nonnegative('Максимальная сумма не может быть отрицательной').optional(),
  createdFrom: z.coerce.date().optional(),
  createdTo: z.coerce.date().optional(),
  search: z.string().optional(),
  sortBy: z.enum(['id', 'createdAt', 'updatedAt', 'statusConfigId', 'total', 'userId']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  page: z.coerce.number().int('Страница должна быть целым числом').positive('Страница должна быть положительной').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').positive('Лимит должен быть положительным').max(100, 'Лимит должен быть <= 100').default(20),
  showDeleted: stringToBooleanSchema,
});

// CRM Schemas
export const changeStatusCrmSchema = z.object({
  statusId: z.number()
    .int('ID статуса должен быть целым числом')
    .positive('ID статуса должен быть положительным'),
  note: z.string().max(2000, 'Заметка слишком длинная (максимум 2000 символов)').optional(),
  confirmed: z.boolean().optional(),
});

export const assignAdminSchema = z.object({
  adminId: z.number().int('ID администратора должен быть целым числом').positive('ID администратора должен быть положительным').nullable(),
});

export const createNoteSchema = z.object({
  content: z.string()
    .min(1, 'Содержимое заметки не может быть пустым')
    .max(5000, 'Заметка слишком длинная (максимум 5000 символов)'),
});

export const updateNoteSchema = z.object({
  content: z.string()
    .min(1, 'Содержимое заметки не может быть пустым')
    .max(5000, 'Заметка слишком длинная (максимум 5000 символов)')
    .optional(),
  isPinned: z.boolean().optional(),
});

export const kanbanFiltersSchema = z.object({
  assignedAdminId: z.coerce.number().int('ID администратора должен быть целым числом').positive('ID администратора должен быть положительным').optional(),
  currency: z.string().trim().min(1).max(10).optional(),
  targetType: orderTargetTypeEnum.optional(),
  targetId: z.coerce.number().int('ID товара должен быть целым числом').positive('ID товара должен быть положительным').optional(),
  userId: z.coerce.number().int('ID пользователя должен быть целым числом').positive('ID пользователя должен быть положительным').optional(),
  search: z.string().trim().min(1).max(200).optional(),
  createdFrom: z.coerce.date().optional(),
  createdTo: z.coerce.date().optional(),
});

export const kanbanColumnOrdersSchema = kanbanFiltersSchema.extend({
  offset: z.coerce.number().int().nonnegative().default(0),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export const kanbanTargetOptionsSchema = z.object({
  targetType: orderTargetTypeEnum,
  search: z.string().trim().min(1).max(200).optional(),
  limit: z.coerce.number().int().positive().max(100).default(50),
});

export const bulkAssignAdminSchema = z.object({
  ids: z
    .array(z.number().int('ID должен быть целым числом').positive('ID должен быть положительным'))
    .min(1, 'Список ID не может быть пустым')
    .max(100, 'Нельзя обработать более 100 записей за раз'),
  assignedAdminId: z.number()
    .int('ID администратора должен быть целым числом')
    .positive('ID администратора должен быть положительным')
    .nullable(),
});

export type OrderItemInput = z.infer<typeof orderItemSchema>;
export type CreateOrderInput = z.infer<typeof createOrderSchema>;
export type CreateMyOrderInput = z.infer<typeof createMyOrderSchema>;
export type UpdateOrderInput = z.infer<typeof updateOrderSchema>;
export type OrderFilters = z.infer<typeof orderFiltersSchema>;
export type ChangeStatusCrmInput = z.infer<typeof changeStatusCrmSchema>;
export type AssignAdminInput = z.infer<typeof assignAdminSchema>;
export type BulkAssignAdminInput = z.infer<typeof bulkAssignAdminSchema>;
export type CreateNoteInput = z.infer<typeof createNoteSchema>;
export type UpdateNoteInput = z.infer<typeof updateNoteSchema>;
export type KanbanFilters = z.infer<typeof kanbanFiltersSchema>;
export type KanbanColumnOrdersInput = z.infer<typeof kanbanColumnOrdersSchema>;
export type KanbanTargetOptionsInput = z.infer<typeof kanbanTargetOptionsSchema>;
